import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI, monitoringAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import {
  PieChart, Pie, Cell,
  AreaChart, Area,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#2563EB', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#8B5CF6'];

interface PageDetailData {
  pageUrl: string;
  pageTitle: string;
  summary: {
    totalViews: number;
    uniqueVisitors: number;
    avgLoadTime: number;
    avgTimeOnPage: number;
    bounceRate: number;
    exitRate: number;
  };
  performance: {
    minLoadTime: number;
    maxLoadTime: number;
    avgLoadTime: number;
    medianLoadTime: number;
  };
  healthCheckMetrics: {
    avgResponseTime: number;
    minResponseTime: number;
    maxResponseTime: number;
    uptimePercentage: number;
    totalChecks: number;
  } | null;
  yearOverYear: {
    currentViews: number;
    lastYearViews: number;
    viewsChange: number;
    currentVisitors: number;
    lastYearVisitors: number;
    visitorsChange: number;
  } | null;
  countries: Array<{ country: string; count: number }>;
  devices: Array<{ device: string; count: number }>;
  browsers: Array<{ browser: string; count: number }>;
  operatingSystems: Array<{ os: string; count: number }>;
  referrers: Array<{ source: string; count: number }>;
  dailyTrend: Array<{ date: string; views: number; uniqueVisitors: number }>;
  monthlyTrend: Array<{ month: string; views: number; uniqueVisitors: number }>;
}

const PageDetailView: React.FC = () => {
  const { pageUrl } = useParams<{ pageUrl: string }>();
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [pageDetail, setPageDetail] = useState<PageDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [showMonthly, setShowMonthly] = useState(false);

  useDocumentTitle(pageDetail?.pageTitle || 'Page Analytics');

  useEffect(() => {
    if (currentAsset && pageUrl) {
      fetchPageDetail();
    }
  }, [currentAsset, pageUrl, period]);

  const fetchPageDetail = async () => {
    if (!currentAsset || !pageUrl) return;

    setLoading(true);
    try {
      const response = await analyticsAPI.getPageDetail(currentAsset.id, decodeURIComponent(pageUrl), period);
      setPageDetail(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch page detail:', error);
      toast.error('Failed to load page analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => new Intl.NumberFormat().format(num);

  const formatLoadTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimeOnPage = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr: any, payload?: any): string => {
    if (!dateStr || typeof dateStr !== 'string') return String(dateStr || '');
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMonth = (monthStr: any, payload?: any): string => {
    if (!monthStr || typeof monthStr !== 'string') return String(monthStr || '');
    const [year, month] = monthStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const getPerformanceColor = (ms: number) => {
    if (ms < 1000) return 'success';
    if (ms < 3000) return 'warning';
    return 'danger';
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return 'bi-arrow-up-circle-fill text-success';
    if (change < 0) return 'bi-arrow-down-circle-fill text-danger';
    return 'bi-dash-circle text-secondary';
  };

  const getDeviceIcon = (device: string) => {
    const d = device?.toLowerCase() || '';
    if (d.includes('mobile')) return 'bi-phone';
    if (d.includes('tablet')) return 'bi-tablet';
    return 'bi-laptop';
  };

  const getBrowserIcon = (browser: string) => {
    const b = browser?.toLowerCase() || '';
    if (b.includes('chrome')) return 'bi-browser-chrome';
    if (b.includes('firefox')) return 'bi-browser-firefox';
    if (b.includes('safari')) return 'bi-browser-safari';
    if (b.includes('edge')) return 'bi-browser-edge';
    return 'bi-globe';
  };

  const getSourceIcon = (source: string) => {
    const s = source?.toLowerCase() || '';
    if (s === 'direct') return 'bi-arrow-right-circle';
    if (s === 'google') return 'bi-google';
    if (s === 'facebook') return 'bi-facebook';
    if (s === 'twitter') return 'bi-twitter-x';
    if (s === 'linkedin') return 'bi-linkedin';
    if (s === 'instagram') return 'bi-instagram';
    return 'bi-link-45deg';
  };

  // Prepare chart data
  const trendData = showMonthly
    ? (pageDetail?.monthlyTrend || []).map(m => ({ ...m, date: m.month }))
    : (pageDetail?.dailyTrend || []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading page analytics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!pageDetail) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <i className="bi bi-exclamation-triangle fs-1 text-warning d-block mb-3"></i>
            <h4>Page Not Found</h4>
            <p className="text-muted">Could not load analytics for this page</p>
            <button className="btn btn-primary" onClick={() => navigate('/analytics/detailed')}>
              <i className="bi bi-arrow-left me-2"></i>Back to Pages
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <button
                  className="btn btn-outline-secondary btn-sm mb-2"
                  onClick={() => navigate('/analytics/detailed')}
                >
                  <i className="bi bi-arrow-left me-2"></i>Back to All Pages
                </button>
                <h2 className="h3 mb-1">{pageDetail.pageTitle}</h2>
                <p className="text-muted mb-0 text-break">{pageDetail.pageUrl}</p>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <div className="btn-group">
                  <button
                    className={`btn btn-sm ${period === '7d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('7d')}
                  >
                    7 Days
                  </button>
                  <button
                    className={`btn btn-sm ${period === '30d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('30d')}
                  >
                    30 Days
                  </button>
                  <button
                    className={`btn btn-sm ${period === '90d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPeriod('90d')}
                  >
                    90 Days
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-3">
                <h4 className="text-primary mb-1">{formatNumber(pageDetail.summary.totalViews)}</h4>
                <small className="text-muted">Total Views</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-3">
                <h4 className="text-success mb-1">{formatNumber(pageDetail.summary.uniqueVisitors)}</h4>
                <small className="text-muted">Unique Visitors</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className={`card border-0 shadow-sm h-100`}>
              <div className="card-body text-center py-3">
                <h4 className={`text-${getPerformanceColor(pageDetail.summary.avgLoadTime)} mb-1`}>
                  {formatLoadTime(pageDetail.summary.avgLoadTime)}
                </h4>
                <small className="text-muted">Avg Load Time</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-3">
                <h4 className="text-info mb-1">{formatTimeOnPage(pageDetail.summary.avgTimeOnPage)}</h4>
                <small className="text-muted">Avg Time on Page</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-3">
                <h4 className="text-warning mb-1">{pageDetail.summary.bounceRate}%</h4>
                <small className="text-muted">Bounce Rate</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-3">
                <h4 className="text-secondary mb-1">{pageDetail.summary.exitRate}%</h4>
                <small className="text-muted">Exit Rate</small>
              </div>
            </div>
          </div>
        </div>

        {/* Performance & Health Metrics */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-speedometer2 me-2"></i>
                  Page Load Performance
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-6 col-md-3">
                    <div className="d-flex align-items-center">
                      <div className="me-2">
                        <i className="bi bi-arrow-down-circle text-success fs-4"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Min Load</small>
                        <strong>{formatLoadTime(pageDetail.performance.minLoadTime)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="d-flex align-items-center">
                      <div className="me-2">
                        <i className="bi bi-arrow-up-circle text-danger fs-4"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Max Load</small>
                        <strong>{formatLoadTime(pageDetail.performance.maxLoadTime)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="d-flex align-items-center">
                      <div className="me-2">
                        <i className="bi bi-dash-circle text-primary fs-4"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Avg Load</small>
                        <strong>{formatLoadTime(pageDetail.performance.avgLoadTime)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="d-flex align-items-center">
                      <div className="me-2">
                        <i className="bi bi-distribute-vertical text-info fs-4"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Median</small>
                        <strong>{formatLoadTime(pageDetail.performance.medianLoadTime)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-heart-pulse me-2"></i>
                  Health Check Metrics
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.healthCheckMetrics ? (
                  <div className="row g-3">
                    <div className="col-6 col-md-4">
                      <div className="text-center">
                        <h4 className={`mb-1 text-${pageDetail.healthCheckMetrics.uptimePercentage >= 99 ? 'success' : pageDetail.healthCheckMetrics.uptimePercentage >= 95 ? 'warning' : 'danger'}`}>
                          {pageDetail.healthCheckMetrics.uptimePercentage}%
                        </h4>
                        <small className="text-muted">Uptime</small>
                      </div>
                    </div>
                    <div className="col-6 col-md-4">
                      <div className="text-center">
                        <h4 className="mb-1 text-primary">{pageDetail.healthCheckMetrics.avgResponseTime}ms</h4>
                        <small className="text-muted">Avg Response</small>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="text-center">
                        <h4 className="mb-1 text-info">{formatNumber(pageDetail.healthCheckMetrics.totalChecks)}</h4>
                        <small className="text-muted">Total Checks</small>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="bi bi-heart-pulse fs-3 d-block mb-2"></i>
                    <p className="mb-0">No health check data available</p>
                    <small>Health metrics appear when URL is monitored</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-graph-up me-2"></i>
                  Views Trend
                </h5>
                <div className="btn-group btn-group-sm">
                  <button
                    className={`btn ${!showMonthly ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setShowMonthly(false)}
                  >
                    Daily
                  </button>
                  <button
                    className={`btn ${showMonthly ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setShowMonthly(true)}
                  >
                    Monthly
                  </button>
                </div>
              </div>
              <div className="card-body">
                {trendData.length > 0 ? (
                  <div style={{ height: '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorPageViews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => (showMonthly ? formatMonth(value) : formatDate(value))}
                          tick={{ fill: '#6b7280', fontSize: 11 }}
                        />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <Tooltip
                          labelFormatter={(label) => (showMonthly ? formatMonth(label) : formatDate(label))}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="views"
                          name="Views"
                          stroke="#3b82f6"
                          fillOpacity={1}
                          fill="url(#colorPageViews)"
                        />
                        <Line
                          type="monotone"
                          dataKey="uniqueVisitors"
                          name="Unique Visitors"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-graph-up fs-1 d-block mb-3"></i>
                    <p>No trend data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Geographic & Device Distribution */}
        <div className="row g-4 mb-4">
          {/* Countries */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-globe me-2"></i>
                  Countries
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.countries.length > 0 ? (
                  <>
                    <div style={{ height: '180px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pageDetail.countries}
                            dataKey="count"
                            nameKey="country"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                          >
                            {pageDetail.countries.map((_, index) => (
                              <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="list-group list-group-flush mt-2">
                      {pageDetail.countries.slice(0, 5).map((c, i) => (
                        <li key={i} className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                          <span>
                            <span className="badge me-2" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length], width: '10px', height: '10px', padding: 0 }}></span>
                            {c.country}
                          </span>
                          <span className="badge bg-primary rounded-pill">{c.count}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-geo-alt fs-3 d-block mb-2"></i>
                    <small>No country data available</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Devices */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-laptop me-2"></i>
                  Devices
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.devices.length > 0 ? (
                  <>
                    <div style={{ height: '180px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pageDetail.devices}
                            dataKey="count"
                            nameKey="device"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                          >
                            {pageDetail.devices.map((_, index) => (
                              <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="list-group list-group-flush mt-2">
                      {pageDetail.devices.map((d, i) => (
                        <li key={i} className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                          <span>
                            <i className={`${getDeviceIcon(d.device)} me-2`}></i>
                            {d.device || 'Unknown'}
                          </span>
                          <span className="badge bg-success rounded-pill">{d.count}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-device-ssd fs-3 d-block mb-2"></i>
                    <small>No device data available</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Browsers */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-browser-chrome me-2"></i>
                  Browsers
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.browsers.length > 0 ? (
                  <ul className="list-group list-group-flush">
                    {pageDetail.browsers.slice(0, 6).map((b, i) => (
                      <li key={i} className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                        <span>
                          <i className={`${getBrowserIcon(b.browser)} me-2`}></i>
                          {b.browser || 'Unknown'}
                        </span>
                        <span className="badge bg-info rounded-pill">{b.count}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-browser-chrome fs-3 d-block mb-2"></i>
                    <small>No browser data available</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Traffic Sources & Year-over-Year */}
        <div className="row g-4 mb-4">
          {/* Traffic Sources */}
          <div className="col-12 col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-signpost-2 me-2"></i>
                  Traffic Sources
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.referrers.length > 0 ? (
                  <div style={{ height: '250px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={pageDetail.referrers.slice(0, 6)}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis
                          dataKey="source"
                          type="category"
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                          width={80}
                        />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                        <Bar dataKey="count" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-signpost fs-1 d-block mb-3"></i>
                    <p>No referrer data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Year-over-Year */}
          <div className="col-12 col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-calendar-range me-2"></i>
                  Year-over-Year Comparison
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.yearOverYear ? (
                  <>
                    <div style={{ height: '180px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            { metric: 'Views', 'This Year': pageDetail.yearOverYear.currentViews, 'Last Year': pageDetail.yearOverYear.lastYearViews },
                            { metric: 'Visitors', 'This Year': pageDetail.yearOverYear.currentVisitors, 'Last Year': pageDetail.yearOverYear.lastYearVisitors }
                          ]}
                          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="metric" tick={{ fill: '#6b7280', fontSize: 12 }} />
                          <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                          <Legend />
                          <Bar dataKey="This Year" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Last Year" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="row mt-3">
                      <div className="col-6 text-center">
                        <small className="text-muted d-block">Views Change</small>
                        <span className={pageDetail.yearOverYear.viewsChange >= 0 ? 'text-success' : 'text-danger'}>
                          <i className={getChangeIcon(pageDetail.yearOverYear.viewsChange) + ' me-1'}></i>
                          {pageDetail.yearOverYear.viewsChange >= 0 ? '+' : ''}{pageDetail.yearOverYear.viewsChange}%
                        </span>
                      </div>
                      <div className="col-6 text-center">
                        <small className="text-muted d-block">Visitors Change</small>
                        <span className={pageDetail.yearOverYear.visitorsChange >= 0 ? 'text-success' : 'text-danger'}>
                          <i className={getChangeIcon(pageDetail.yearOverYear.visitorsChange) + ' me-1'}></i>
                          {pageDetail.yearOverYear.visitorsChange >= 0 ? '+' : ''}{pageDetail.yearOverYear.visitorsChange}%
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-calendar-x fs-1 d-block mb-3"></i>
                    <p>Year-over-year data not available</p>
                    <small>Data will appear after one year of tracking</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Operating Systems */}
        <div className="row g-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-cpu me-2"></i>
                  Operating Systems
                </h5>
              </div>
              <div className="card-body">
                {pageDetail.operatingSystems.length > 0 ? (
                  <div style={{ height: '200px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={pageDetail.operatingSystems.slice(0, 6)}
                        margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis
                          dataKey="os"
                          type="category"
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                          width={70}
                        />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                        <Bar dataKey="count" fill="#06B6D4" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-cpu fs-3 d-block mb-2"></i>
                    <p>No OS data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PageDetailView;
